import {
  type ConfirmationResult,
  signOut as fbSignOut,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signInWithPopup,
  type User,
} from "firebase/auth";
import { useCallback, useEffect, useRef, useState } from "react";
import { auth } from "../firebase";
import { e164 } from "./phone";

/**
 * The signed-in session. Google, Apple, or a phone number.
 *
 * Anonymous sign-in was the original design and has been removed — see
 * docs/decisions/0011-google-only-auth.md, which supersedes ADR 0007. One
 * identity, present from the first screen, means there is no orphaned-data
 * failure mode and no upgrade path to build and explain.
 *
 * Google-ONLY is over; see docs/decisions/0016-three-ways-in.md. The reason it
 * changed is reach rather than preference: a household in India is far more
 * likely to have a phone number than a Google account it remembers the password
 * to, and iOS reviewers require Apple alongside other social logins. What did
 * NOT change is that every account is a real identity from the first screen.
 *
 * The one thing that ripples outward: a phone account has no email address.
 * `firestore.rules` and the spend gate both used to key on email, and both now
 * accept a phone number instead — a signed-in user who cannot write their own
 * data is a worse bug than no phone sign-in at all.
 */

export type Session =
  | { state: "loading" }
  | { state: "signed-out" }
  | { state: "signed-in"; uid: string; user: User }
  | { state: "error"; message: string };

export interface SessionApi {
  session: Session;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  /** Sends the six-digit code. Resolves true when the code is on its way. */
  sendPhoneCode: (phone: string) => Promise<boolean>;
  /** Confirms the code from the last `sendPhoneCode`. */
  confirmPhoneCode: (code: string) => Promise<void>;
  /** True once a code has been sent and is waiting to be typed in. */
  awaitingCode: boolean;
  /** Abandons a half-finished phone sign-in. */
  cancelPhone: () => void;
  signOut: () => Promise<void>;
  busy: boolean;
  error: string | null;
}

export function useSession(): SessionApi {
  const [session, setSession] = useState<Session>({ state: "loading" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [awaitingCode, setAwaitingCode] = useState(false);
  const confirmation = useRef<ConfirmationResult | null>(null);
  const verifier = useRef<RecaptchaVerifier | null>(null);

  useEffect(
    () =>
      onAuthStateChanged(
        auth,
        (user) => {
          setSession(
            user ? { state: "signed-in", uid: user.uid, user } : { state: "signed-out" },
          );
        },
        (e) => setSession({ state: "error", message: clean(e.message) }),
      ),
    [],
  );

  // The reCAPTCHA widget outlives a re-render but must not outlive the screen:
  // a stale verifier attached to a detached DOM node fails on the second
  // attempt with an error that reads like a network problem.
  useEffect(
    () => () => {
      verifier.current?.clear();
      verifier.current = null;
    },
    [],
  );

  const run = useCallback(async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      return true;
    } catch (e) {
      const err = e as { code?: string; message: string };
      // Closing the sign-in window is a decision, not a failure.
      if (
        err.code === "auth/popup-closed-by-user" ||
        err.code === "auth/cancelled-popup-request"
      ) {
        return false;
      }
      setError(clean(err.message));
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const signInWithGoogle = useCallback(
    () =>
      run(() => signInWithPopup(auth, new GoogleAuthProvider())).then(() => undefined),
    [run],
  );

  /**
   * Apple hands back a name on the FIRST sign-in only, and only if these scopes
   * are asked for. Miss them and the account is permanently nameless — there is
   * no second chance short of the user revoking the app in iOS settings.
   */
  const signInWithApple = useCallback(() => {
    const provider = new OAuthProvider("apple.com");
    provider.addScope("email");
    provider.addScope("name");
    return run(() => signInWithPopup(auth, provider)).then(() => undefined);
  }, [run]);

  const sendPhoneCode = useCallback(
    async (phone: string) => {
      // Invisible reCAPTCHA: required by Firebase on the web, and it only shows
      // itself when a request looks automated.
      if (!verifier.current) {
        verifier.current = new RecaptchaVerifier(auth, "recaptcha-holder", {
          size: "invisible",
        });
      }
      const ok = await run(async () => {
        const v = verifier.current;
        if (!v) throw new Error("Sign-in isn't ready yet. Try again.");
        confirmation.current = await signInWithPhoneNumber(auth, e164(phone), v);
      });
      if (ok) setAwaitingCode(true);
      else {
        // A spent challenge cannot be reused; the next attempt needs a new one.
        verifier.current?.clear();
        verifier.current = null;
      }
      return ok;
    },
    [run],
  );

  const confirmPhoneCode = useCallback(
    async (code: string) => {
      const ok = await run(async () => {
        const c = confirmation.current;
        if (!c) throw new Error("That code has expired. Ask for a new one.");
        await c.confirm(code.trim());
      });
      if (ok) {
        confirmation.current = null;
        setAwaitingCode(false);
      }
    },
    [run],
  );

  const cancelPhone = useCallback(() => {
    confirmation.current = null;
    setAwaitingCode(false);
    setError(null);
  }, []);

  const signOut = useCallback(
    () => run(() => fbSignOut(auth)).then(() => undefined),
    [run],
  );

  return {
    session,
    signInWithGoogle,
    signInWithApple,
    sendPhoneCode,
    confirmPhoneCode,
    awaitingCode,
    cancelPhone,
    signOut,
    busy,
    error,
  };
}

/**
 * Firebase auth errors read "Firebase: Error (auth/some-code)." — stripping the
 * code leaves the literal word "Error", which is what shipped to production and
 * told the user nothing at all. The CODE is the only useful part, so it gets
 * translated rather than discarded.
 */
const AUTH_MESSAGES: Record<string, string> = {
  "auth/operation-not-allowed": "That way of signing in is turned off for this app.",
  "auth/network-request-failed": "No connection. Check your network and try again.",
  "auth/popup-blocked":
    "Your browser blocked the sign-in window. Allow pop-ups and retry.",
  "auth/unauthorized-domain": "This site isn't authorised for sign-in yet.",
  "auth/too-many-requests": "Too many attempts. Wait a minute and try again.",
  "auth/user-disabled": "That account has been disabled.",
  "auth/invalid-phone-number": "That doesn't look like a complete phone number.",
  "auth/missing-phone-number": "Type your phone number first.",
  "auth/invalid-verification-code": "That code doesn't match. Check it and try again.",
  "auth/code-expired": "That code has expired. Ask for a new one.",
  "auth/quota-exceeded": "Too many codes sent right now. Try again in a few minutes.",
  "auth/captcha-check-failed": "That check didn't pass. Reload the page and try again.",
};

function clean(message: string): string {
  const code = /\((auth\/[a-z-]+)\)/.exec(message)?.[1];

  /**
   * `auth/operation-not-allowed` is overloaded, and the difference matters.
   *
   * Firebase returns it both when the provider is genuinely switched off AND
   * when the provider is on but the SMS REGION POLICY excludes the caller's
   * country — a separate setting, on a separate tab, that new projects start
   * locked down for anti-fraud reasons. Collapsing the two sent the first
   * person who hit it to the provider toggle, which was already correct.
   *
   * The underlying text is the only thing that distinguishes them, so read it
   * before falling back to the generic mapping.
   */
  if (code === "auth/operation-not-allowed" && /region/i.test(message)) {
    return "Text messages aren't switched on for your country yet. Use Google or Apple for now.";
  }

  if (code && AUTH_MESSAGES[code]) return AUTH_MESSAGES[code];
  const stripped = message.replace(/^Firebase:\s*/, "").trim();
  // Never return a bare "Error" — say something the user can act on.
  return code && stripped.replace(/\s*\(auth\/.*$/, "").trim().length < 8
    ? `Sign-in failed (${code.replace("auth/", "")}).`
    : stripped;
}
